import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { SearchIcon, EditIcon, DeleteIcon, ArrowLeftIcon, ArrowRightIcon, ReactivateIcon } from "../assets/dashboard-icon.svg";
import { useRef, useState } from "react";
import { Freelancer } from "../api/customer/index";
import { useFreelancers } from "../hooks/useFreelancers";
import { DashboardButton } from "./dashboard/dashboard-button.component";
import AddFreelancerModal from "../components/add-freelancer-modal/index";
import LoadingCard from '../components/SkeletonLoading'

export const MyFreelancerSection = ({
    className,
}: {
    className?: string
}
) => {
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;
    const [isFreelancerModal, setIsFreelancerModal] = useState(false)
    const {
        freelancers: paginatedFreelancers,
        loading,
        error,
        searchQuery,
        currentPage,
        totalPages,
        setSearchQuery,
        setCurrentPage,
        goToNextPage,
        goToPreviousPage,
        // Reactivate freelancer functionality
        isReactivate,
        setIsReactivate,
        handleReactivateFreelancer,
        reactivateLoading,
    } = useFreelancers(10);
    const PAGE_SIZE = 10;

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const onReactivateFreelancer = (id: number) => {
        handleReactivateFreelancer(id, t('FREELANCER_REACTIVATED_SUCCESS'));
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };
    if (loading) {
        return (
            <div className={`invoice-table-container ${className}`}>
                <div className="invoice-table-header">
                    <h3 className="invoice-table-title">{t('MY_INVOICES')}</h3>
                </div>
                <LoadingCard/>
            </div>
        );
    }

    return (
        <div className={`invoice-table-container my-3 ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">{t('MY_FREELANCERS')}</h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('HEADER_SEARCH') || 'Search'}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />

                    </div>
                    <DashboardButton
                        variant="primary"
                        // icon={<PlusIcon />}
                        onClick={() => setIsFreelancerModal(true)}
                    >
                        {t('ADD_FREELANCER')}
                    </DashboardButton>
                </div>
            </div>

            <div className="invoice-table-content">


                {/* Table */}
                {!loading && !error && paginatedFreelancers.length >= 0 && (
                    <div className="freelancer-table-scroll-wrapper">
                        <div className="invoice-table">
                            {!isMobile && (
                                <TableHeader>
                                    <TableColumn>{t('SERIAL_ID')}</TableColumn>
                                    <TableColumn>{t('USER_ID')}</TableColumn>
                                    <TableColumn>{t('FREELANCER_NAME')}</TableColumn>
                                    <TableColumn>{t('EMAIL')}</TableColumn>
                                    <TableColumn>{t('PHONE_NO')}</TableColumn>
                                    <TableColumn>{t('STATUS')}</TableColumn>
                                    <TableColumn>{t('ACTION_LABEL')}</TableColumn>
                                </TableHeader>
                            )}

                            <TableBody>
                                {/* Loading State */}
                                {loading && (
                                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                        {t('LOADING_FREELANCERS') || 'Loading freelancers...'}
                                    </div>
                                )}
                                {paginatedFreelancers.length === 0 && (
                                    <div className="invoice-table">
                                        <div className="invoice-table-content">
                                            {(paginatedFreelancers.length === 0) && (
                                                <div className='d-flex justify-content-center flex-column align-items-center pt-5'>
                                                    <span className='pt-3'>{t('NO_FREELANECER_DATA_FOUND')}.</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {isMobile ? (
                                    // Mobile: Card-based layout
                                    paginatedFreelancers?.map((freelancer: Freelancer, index) => (
                                        <MobileCardWrapper key={freelancer.id}>
                                            <MobileCardRow label={t('SERIAL_ID')}>
                                                <span className="invoice-table-detailed__invoice-link">
                                                    {(currentPage - 1) * PAGE_SIZE + index + 1}
                                                </span>
                                            </MobileCardRow>
                                            <MobileCardRow label={t('USER_ID')}>
                                                <span className="invoice-table-detailed__invoice-link">{freelancer?.signUpUser.userId || 'N?A'}</span>
                                            </MobileCardRow>
                                            <MobileCardRow label={t('FREELANCER_NAME')}>
                                                {freelancer.signUpUser.firstName || 'N/A'}
                                            </MobileCardRow>
                                            <MobileCardRow label={t('EMAIL')}>
                                                {freelancer.signUpUser.email || 'N/A'}
                                            </MobileCardRow>
                                            <MobileCardRow label={t('PHONE_NO')}>
                                                {freelancer.signUpUser.phoneNo || 'N/A'}
                                            </MobileCardRow>
                                            <MobileCardRow label={t('STATUS')}>
                                                {freelancer.signUpUser.isActive ? 'Active' : 'Inactive'}
                                            </MobileCardRow>
                                            <MobileCardRow label={t('REACTIVATE_FREELANCER')}>
                                                <div className="invoice-table-detailed__action-group">
                                                    <button
                                                        className="invoice-table-detailed__action-btn"
                                                        title={t("REACTIVATE")}
                                                        disabled={freelancer.signUpUser.isActive}
                                                        onClick={() => {
                                                            setIsReactivate({
                                                                show: true,
                                                                id: Number(freelancer.signUpUser.userId) || 0,
                                                                name: freelancer.signUpUser.firstName || '',
                                                            });
                                                            onReactivateFreelancer(Number(freelancer.signUpUser.userId) || 0);
                                                        }}

                                                    >
                                                        <ReactivateIcon opacity={freelancer.signUpUser.isActive ? 0.5 : 1} />
                                                    </button>

                                                </div>
                                            </MobileCardRow>
                                        </MobileCardWrapper>
                                    ))
                                ) : (
                                    paginatedFreelancers.map((freelancer: Freelancer, index) => (
                                        <TableRow key={freelancer.id}>
                                            <TableCell>
                                           {(currentPage - 1) * PAGE_SIZE + index + 1}
                                            </TableCell>
                                            <TableCell>{freelancer.signUpUser.userId || 'N/A'}</TableCell>
                                            <TableCell>{freelancer.signUpUser.firstName || 'N/A'}</TableCell>
                                            <TableCell>{freelancer.signUpUser.email || 'N/A'}</TableCell>
                                            <TableCell>{freelancer.signUpUser.phoneNo || 'N/A'}</TableCell>
                                            <TableCell>{freelancer.signUpUser.isActive ? 'Active' : 'Inactive'}</TableCell>
                                            <TableCell>
                                                <div className="invoice-table-detailed__action-group">
                                                    <button
                                                        className="invoice-table-detailed__action-btn"
                                                        title={t("REACTIVATE_FREELANCER")}
                                                        disabled={freelancer.signUpUser.isActive}
                                                        onClick={() => {
                                                            setIsReactivate({
                                                                show: true,
                                                                id: Number(freelancer.signUpUser.userId) || 0,
                                                                name: freelancer.signUpUser.firstName || '',
                                                            });
                                                            onReactivateFreelancer(Number(freelancer.signUpUser.userId) || 0);
                                                        }}
                                                    >
                                                        <ReactivateIcon opacity={freelancer.signUpUser.isActive ? 0.5 : 1} />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP') || 'Prev'}</span>
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                    </div>
                )}
                 <AddFreelancerModal show={isFreelancerModal} onClose={() => setIsFreelancerModal(false)}/>
            </div>
        </div>
    );
}